import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { api } from "../../api";
import { refPath } from "../../components/RefChip";
import { Card, EmptyState } from "../../shell/ui";
import { StrategyPage } from "./StrategyPage";

interface Resolved {
  strategyId: string;
  namespace: string;
  slug: string;
  renamedFrom: string | null;
  version: number | null;
  ref: string;
}

/**
 * `/s/alex/rsi-dip:v2` (or `@sha256:…`): resolve the reference, then show the
 * strategy with the named version marked. A renamed slug is answered by the
 * strategy's current name, and the address bar is corrected to it.
 */
export function RefStrategyPage() {
  const { namespace = "", ref = "" } = useParams();
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ["hub-ref", namespace, ref],
    queryFn: () => api<Resolved>(`/hub/ref/${encodeURIComponent(namespace)}/${encodeURIComponent(ref)}`),
    retry: false,
  });

  useEffect(() => {
    if (q.data?.renamedFrom) {
      const pinned = ref.includes(":") || ref.includes("@") ? q.data.version : null;
      navigate(refPath(q.data.namespace, q.data.slug, pinned), { replace: true });
    }
  }, [q.data, ref, navigate]);

  if (q.isLoading)
    return (
      <p className="muted">
        Looking up {namespace}/{ref}…
      </p>
    );
  if (q.error || !q.data)
    return (
      <Card>
        <EmptyState icon="◇" title={`${namespace}/${ref}`}>
          {q.error?.message ?? "No such strategy."}
        </EmptyState>
      </Card>
    );
  const pinned = ref.includes(":") || ref.includes("@");
  return <StrategyPage strategyId={q.data.strategyId} highlightVersion={pinned ? q.data.version : null} />;
}
